import { fetchPublicJson } from "../lib/data-client.mjs";
import { useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown, ChevronUp, ExternalLink, Loader2, TriangleAlert } from "lucide-react";

const TRAFFIC_FALLBACK_URL = "https://trafikkort.vejdirektoratet.dk/index.html";
const VD_LOGO_URL = "https://www.vejdirektoratet.dk/static/images/logo.svg";

function activeItems(data) {
  return (Array.isArray(data?.items) ? data.items : [])
    .filter((item) => item?.title && item?.description)
    .slice(0, 3);
}

export function TrafficInfoWidget() {
  const [data, setData] = useState(null);
  const [status, setStatus] = useState("idle");
  const [isExpanded, setIsExpanded] = useState(false);
  const loadedRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    let timer;
    let controller;
    setStatus("loading");
    const poll = async () => {
      try {
        if (document.hidden && loadedRef.current) return; // always load once, even in a background tab
        controller = new AbortController();
        const nextData = await fetchPublicJson("traffic-info.json", { signal: controller.signal });
        if (!Array.isArray(nextData.items)) throw new Error("Invalid traffic data");
        if (!cancelled) { loadedRef.current = true; setData(nextData); setStatus("ready"); }
      } catch {
        if (!cancelled) setStatus((current) => current === "ready" ? "ready" : "error");
      } finally { if (!cancelled) timer = setTimeout(poll, 60000); }
    };
    poll();
    return () => { cancelled = true; clearTimeout(timer); controller?.abort(); };
  }, []);

  const items = useMemo(() => activeItems(data), [data]);
  if (status === "error" || (status === "ready" && items.length === 0)) return null;

  const primary = items[0];
  const sourceUrl = data?.source_url || primary?.source_url || TRAFFIC_FALLBACK_URL;

  return (
    <div className="px-6 py-3 border-t border-[#E5E5E5] dark:border-zinc-800 bg-[#F7FBFF] dark:bg-sky-950/10">
      <button
        type="button"
        onClick={() => setIsExpanded((value) => !value)}
        className="w-full text-left rounded-sm focus:outline-none"
        aria-expanded={isExpanded}
        data-testid="traffic-info-toggle"
      >
        <div className="flex items-center justify-between gap-2">
          <div className="flex min-w-0 items-center gap-2">
            <span className="inline-flex h-7 w-28 flex-shrink-0 items-center justify-start overflow-hidden">
              <img src={VD_LOGO_URL} alt="" className="h-7 w-auto max-w-none" />
            </span>
          </div>
          {status === "loading" ? (
            <Loader2 className="w-3.5 h-3.5 flex-shrink-0 animate-spin text-[#005EB8]" strokeWidth={1.6} />
          ) : (
            isExpanded ? (
              <ChevronDown className="w-3.5 h-3.5 flex-shrink-0 text-gray-400" strokeWidth={1.6} />
            ) : (
              <ChevronUp className="w-3.5 h-3.5 flex-shrink-0 text-gray-400" strokeWidth={1.6} />
            )
          )}
        </div>

        {status === "loading" && (
          <p className="mt-2 text-[10px] tracking-[0.16em] uppercase font-mono text-gray-400">
            HENTER TRAFIKINFO
          </p>
        )}

        {primary && (
          <div className="mt-2 flex items-start gap-2">
            <TriangleAlert className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-[#005EB8]" strokeWidth={1.7} />
            <div className="min-w-0">
              <p className="text-sm font-semibold leading-snug text-[#0A0A0A] dark:text-zinc-100">
                {primary.title}
              </p>
              {items.length > 1 && (
                <p className="mt-1 text-[10px] tracking-[0.14em] uppercase font-mono text-gray-400">
                  +{items.length - 1} mere
                </p>
              )}
            </div>
          </div>
        )}
      </button>

      {isExpanded && primary && (
        <div className="mt-3 space-y-3">
          {items.map((item) => (
            <div key={item.id} className="border-t border-[#DDE8F3] dark:border-sky-900 pt-3">
              <p className="text-xs font-semibold leading-snug text-[#0A0A0A] dark:text-zinc-100">
                {item.title}
              </p>
              <p className="mt-1 text-xs leading-relaxed text-gray-600 dark:text-gray-300">
                {item.description}
              </p>
            </div>
          ))}
          <a
            href={sourceUrl}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.14em] uppercase font-mono text-[#005EB8] dark:text-sky-300 hover:underline"
          >
            Åbn Trafikkort
            <ExternalLink className="h-3 w-3" strokeWidth={1.6} />
          </a>
        </div>
      )}
    </div>
  );
}
